import { FIELDS } from './fields'

/**
 * Odhadne, kolik řádků na KONCI je souhrn nebo poznámka, ne data.
 *
 * Typický případ: export končí řádkem „CELKEM ; ; ; 2500 ; CZK“. Bez odříznutí
 * projde jako vstupenka s kódem „CELKEM“ — a protože souhrn často nemá kód
 * v očekávané podobě, import buď spadne na validaci, nebo (hůř) založí
 * nesmyslnou vstupenku.
 *
 * Konzervativní záměrně: špatný ořez zahodí skutečná data, což je horší než
 * jeden nesmyslný řádek, kterého si uživatel všimne v náhledu.
 */
export function detectTrailingRows(rows: string[][]): number {
    if (rows.length < 3) return 0

    // Šířka „normálních“ řádků z prostředka souboru — konec i začátek můžou být
    // atypické, takže se na ně neptáme.
    const bodyWidth = medianFilledCount(rows.slice(0, Math.min(rows.length - 2, 30)))
    if (bodyWidth < 2) return 0 // úzká data: šířka nic neřekne

    let trailing = 0
    // Nikdy neořezávat víc než pár řádků ani většinu souboru.
    const limit = Math.min(5, rows.length - 2)

    while (trailing < limit) {
        const row = rows[rows.length - 1 - trailing]
        const filled = filledCount(row)

        // Prázdný řádek na konci se počítá jako součást ocasu.
        if (filled === 0) {
            trailing++
            continue
        }

        // Souhrn má vyplněných výrazně méně buněk než datové řádky.
        if (filled * 2 > bodyWidth) break

        // Musí obsahovat slovo typu „celkem“ / „total“, jinak by se ořízl
        // i legitimní neúplný datový řádek.
        if (!looksLikeSummary(row)) break

        trailing++
    }

    // Samotné prázdné řádky na konci neřeší tahle funkce (odfiltruje je parser),
    // takže pokud jsme nenašli žádný souhrn, nic neořezáváme.
    return hasSummary(rows, trailing) ? trailing : 0
}

/** Prošli jsme aspoň jeden neprázdný řádek vypadající jako souhrn? */
function hasSummary(rows: string[][], trailing: number): boolean {
    for (let i = 0; i < trailing; i++) {
        const row = rows[rows.length - 1 - i]
        if (filledCount(row) > 0 && looksLikeSummary(row)) return true
    }
    return false
}

/**
 * Klíčová slova souhrnných řádků. Bez nich se neořezává — samotná „krátkost“
 * řádku je slabý signál, protože i skutečná vstupenka může mít půlku sloupců
 * prázdných.
 */
const SUMMARY_WORDS = [
    'celkem',
    'celkove',
    'suma',
    'soucet',
    'total',
    'sum',
    'summary',
    'subtotal',
    'gesamt',
    'summe'
]

function looksLikeSummary(row: string[]): boolean {
    // Souhrn nesmí nést známý název sloupce (to by byla hlavička).
    const knownHeaders = new Set(FIELDS.flatMap(f => f.aliases.map(normalise)))

    return row.some(cell => {
        const norm = normalise(cell)
        if (!norm || knownHeaders.has(norm)) return false
        return SUMMARY_WORDS.some(w => norm.startsWith(w))
    })
}

function filledCount(row: string[] | undefined): number {
    return (row ?? []).filter(c => (c ?? '').trim() !== '').length
}

function medianFilledCount(rows: string[][]): number {
    const counts = rows.map(filledCount).filter(n => n > 0).sort((a, b) => a - b)
    if (counts.length === 0) return 0
    return counts[Math.floor(counts.length / 2)]
}

function normalise(value: string): string {
    return (value ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
}
